import {CommonModule, DatePipe} from '@angular/common';
import {Component, computed, effect, input, OnInit, output, signal} from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  Button,
  ButtonComponent,
  ButtonService,
  InputWrapperComponent
} from '@innovactions/frontend-library';
import {FormArray, FormBuilder, UntypedFormGroup} from '@angular/forms';
import {CareerTarget} from '../../../service/career-target';
import {CompanyCareerFunctionLabelPipe} from '../../../pipe/company-career-function-label-pipe';
import {DocumentDto} from '../../../dto/document-dto';
import {ProfileManagementSchema} from '../../../schema/input/profile-management-schema';
import {Profile} from '../../../service/profile';
import {ProfileDto} from '../../../dto/profile-dto';
import {ActivatedRoute} from '@angular/router';
import {SearchObjects} from '../../shared/search-objects/search-objects';
import {environment} from '../../../../environments/environment';
import {ProfileUndefinedException} from "../../../exception/profile-undefined-exception";
import {SelectSectorService} from '../../../service/sector-selection';
import {UserDto} from '../../../dto/user-dto';
import {AccountStatus} from '../../../enum/account-status';
import {Role} from '../../../enum/role';
import {InflowTypePipe} from '../../../pipe/inflow-type-pipe';
import {StatusPipe} from '../../../pipe/status-pipe';
import {SalaryTypePipe} from '../../../pipe/salary-type-pipe';
import {EmploymentTypePipe} from '../../../pipe/employment-type-pipe';
import {User} from '../../../service/user';
import {CareerTargetKind} from '../../../enum/career-target-kind';
import {CareerFunctionDto} from '../../../dto/career-function-dto';
import {CareerFunction} from '../../../service/career-function';
import {CurrentUserService} from '../../../service/current-user';
import {CareerProfileDto} from '../../../dto/career-profile-dto';
import {CareerProfile} from '../../../service/career-profile';
import {ProfileDocumentOptions} from '../profile-document-options/profile-document-options';
import {DocumentType} from '../../../enum/document-type';
import {DocumentLevel} from '../../../enum/document-level';
import {Department} from '../../../service/department';
import {DepartmentDto} from '../../../dto/department-dto';
import {UserOption} from '../../../domain/user-option';

@Component({
  selector: 'app-profile-management',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    InputWrapperComponent,
    SearchObjects,
    ButtonComponent,
    InflowTypePipe,
    StatusPipe,
    SalaryTypePipe,
    EmploymentTypePipe,
    ProfileDocumentOptions
  ],
  providers: [
    CompanyCareerFunctionLabelPipe
  ],
  templateUrl: './profile-management.html',
  styleUrl: './profile-management.scss'
})
export class ProfileManagement implements OnInit {

  updated = output<void>();
  readonlyMode = input<boolean>(false);
  userId = input<string | undefined>();

  protected formGroup = signal<UntypedFormGroup | undefined>(undefined);
  protected profileManagementSchema = new ProfileManagementSchema();
  protected profileManagementButton = new Button(undefined, 'profile-management-button-id', 'w-full btn-primary hover-effect-elevate', 'w-full btn-primary hover-effect-elevate is-disabled', undefined, "Profiel opslaan", false, true);
  private isEditing = false;

  protected selectedProfile = signal<ProfileDto | undefined>(undefined);
  protected defaultCareerTargets = signal<CareerProfileDto[]>([]);
  protected careerTargets = signal<CareerProfileDto[]>([]);
  protected selectedCareerTarget = signal<CareerProfileDto | undefined>(undefined);

  protected defaultCareerFunctions = signal<CareerFunctionDto[]>([]);
  protected searchCareerFunctions = signal<CareerFunctionDto[]>([]);
  protected selectedCareerFunction = signal<CareerFunctionDto | undefined>(undefined);

  protected availablePopPmManagers = signal<Array<UserDto & { name: string } | null>>([]);
  protected searchedPopPmManagers = signal<Array<UserDto & { name: string } | null>>([]);
  protected selectedPopPmManager = signal<(UserDto & { name: string }) | null>(null);
  protected searchedCoaches = signal<UserOption[]>([]);
  protected availableCoaches = signal<UserOption[]>([]);
  protected searchedManagers = signal<UserOption[]>([]);
  protected availableManagers = signal<UserOption[]>([]);
  protected selectedCoach = signal<UserOption | null>(null);
  protected selectedManager = signal<UserOption | null>(null);
  protected departments = signal<DepartmentDto[]>([]);
  protected searchDepartments = signal<DepartmentDto[]>([]);
  protected selectedDepartments = signal<DepartmentDto[]>([]);
  private routeUserId = signal<string | undefined>(undefined);

  private itemsPerPage = environment.itemsPerPage;
  protected readonly DocumentType = DocumentType;
  protected readonly DocumentLevel = DocumentLevel;

  protected coursesAndTrainings = computed(() =>
    this.selectedProfile()?.documents?.filter(doc => doc.type === DocumentType.COURSES_AND_TRAININGS) ?? []);
  protected diplomas = computed(() =>
    this.selectedProfile()?.documents?.filter(doc => doc.type === DocumentType.DIPLOMA) ?? []);
  protected certificates = computed(() =>
    this.selectedProfile()?.documents?.filter(doc => doc.type === DocumentType.CERTIFICATE) ?? []);

  constructor(private readonly sectorSelectorService: SelectSectorService,
              private careerFunctionService: CareerFunction,
              private careerProfileService: CareerProfile,
              private readonly companyCareerFunctionLabelPipe: CompanyCareerFunctionLabelPipe,
              private readonly formBuilder: FormBuilder,
              private readonly careerTargetService: CareerTarget,
              private readonly alertService: AlertService, private readonly buttonService: ButtonService,
              private activatedRoute: ActivatedRoute, private readonly profileService: Profile,
              private readonly userService: User,
              private readonly currentUserService: CurrentUserService,
              private readonly departmentService: Department) {
    this.formGroup.set(this.formBuilder.group({
      documents: this.formBuilder.array<{
        fileUrl: { file: File, fileUrl: string } | null,
        document: DocumentDto,
      }>([])
    }));

    effect(() => {
      const sectorId = this.sectorSelectorService.getSelectedSectorId();
      const companyId = this.sectorSelectorService.getSelectedCompanyId();

      if (!sectorId || !companyId) {
        return;
      }

      if (!this.canViewCareerProfile()) {
        return;
      }

      this.retrieveCareerProfilesByCompanyId(sectorId, companyId);
    });
  }

  ngOnInit() {
    if (this.isReadonly()) {
      this.formGroup()?.disable({emitEvent: false});
    }

    this.activatedRoute.params.subscribe(params => {
      this.routeUserId.set(params['userId']);

      const targetUserId = this.userId() ?? this.routeUserId();

      if (!targetUserId) {
        return;
      }

      this.retrieveProfileById(targetUserId);
    });

    this.retrieveCareerFunctionsByCompanyId();
  }

  protected handleSaveEvent() {
    if (this.formGroup()?.invalid) {
      this.alertService.createAlert(new Alert(AlertType.WARN, "", "Niet alle velden zijn juist ingevuld", false));
      this.buttonService.enableButton(this.profileManagementButton);
      return;
    }

    if (this.isReadonly()) {
      return;
    }

    const profile = this.createProfileEntity();
    const files = this.getDocumentsFilesArray();
    this.profileService.updateProfile(profile).subscribe({
      next: async () => {
        for (const file of files) {
          if (file != null) {
            await fetch(file.url, {
              method: "PUT",
              body: file.file,
            });
          }
        }
        this.alertService.createAlert(new Alert(AlertType.SUCCESS, "Profile aangepast", "Het profiel is succesvol aangepast", false));
        const targetUserId = this.userId() ?? this.routeUserId();
        if (targetUserId) {
          this.retrieveProfileById(targetUserId);
        }
        this.toggleEditing();
      },
      error: () => {
        this.alertService.createAlert(new Alert(AlertType.ERROR, "Profiel opslaan mislukt", "Er is een fout opgetreden bij het opslaan van het profiel", false));
        this.buttonService.enableButton(this.profileManagementButton);
      },
      complete: () => {
        this.buttonService.enableButton(this.profileManagementButton);
        this.updated.emit();
      }
    });
  }

  protected handleQueryChange(query: string, type: 'careerFunction' | 'careerTarget' = 'careerTarget') {
    if (type === 'careerFunction') {
      this.handleCareerFunctionSearch(query);
    } else {
      this.handleCareerTargetSearch(query);
    }
  }

  private handleCareerTargetSearch(query: string) {
    const sectorId = this.sectorSelectorService.getSelectedSectorId();
    const companyId = this.sectorSelectorService.getSelectedCompanyId();

    if (!sectorId || !companyId) {
      this.careerTargets.set([]);
      return;
    }

    this.careerProfileService.retrieveCareerProfilesByQuery(query, sectorId, companyId, this.itemsPerPage, ['id', 'name']).subscribe({
      next: (response) => {
        this.careerTargets.set(response);
      }
    });
  }

  private handleCareerFunctionSearch(query: string) {
    const sectorId = this.sectorSelectorService.getSelectedSectorId();
    const companyId = this.sectorSelectorService.getSelectedCompanyId();

    if (!sectorId || !companyId) {
      this.searchCareerFunctions.set([]);
      return;
    }

    this.careerFunctionService.retrieveCareerFunctionsByQuery(query, companyId, this.itemsPerPage, ['id', 'name']).subscribe({
      next: (response) => {
        this.searchCareerFunctions.set([...response]);
      }
    });
  }

  protected handleCoachSelection(coach: UserOption | null) {
    this.selectedCoach.set(coach);
  }

  protected searchCoaches(query: string) {
    const companyId = this.selectedProfile()?.user?.company?.id;

    if (!companyId) {
      this.searchedCoaches.set([]);
      return;
    }

    this.userService.retrieveUsersByQuery(query, companyId, [Role.COACH], ['id', 'firstname', 'lastname', 'roles']).subscribe({
      next: (response) => {
        this.searchedCoaches.set(response.map((user) => this.mapUserToOption(user)));
      },
      error: () => {
        this.searchedCoaches.set([]);
      }
    });
  }

  protected searchManagers(query: string) {
    const companyId = this.selectedProfile()?.user?.company?.id;

    if (!companyId) {
      this.searchedManagers.set([]);
      return;
    }

    this.userService.retrieveUsersByQuery(query, companyId, [Role.MANAGER], ['id', 'firstname', 'lastname', 'roles']).subscribe({
      next: (response) => {
        this.searchedManagers.set(response.map((user) => this.mapUserToOption(user)));
      },
      error: () => {
        this.searchedManagers.set([]);
      }
    });
  }

  protected handleDepartmentQueryChange(query: string) {
    const term = query.trim().toLowerCase();

    if (!term) {
      this.searchDepartments.set(this.departments());
      return;
    }

    this.searchDepartments.set(this.departments().filter(department =>
      department.name.toLowerCase().includes(term)));
  }

  protected handleDepartmentSelection(departments: DepartmentDto[]): void {
    this.selectedDepartments.set(departments);
  }

  protected handlePopPmManagerSelection(popPmManager: (UserDto & { name: string }) | null) {
    this.selectedPopPmManager.set(popPmManager);
  }

  protected searchPopPmManagers(query: string) {
    const companyId = this.selectedProfile()?.user?.company?.id;

    if (!companyId) {
      this.searchedPopPmManagers.set([]);
      return;
    }

    this.userService.retrieveUsersByQuery(query, companyId, [Role.POPPMMANAGER], ['id', 'firstname', 'lastname', 'email', 'roles']).subscribe({
      next: (response) => {
        this.searchedPopPmManagers.set(response
          .filter((popPmManager): popPmManager is UserDto => !!popPmManager?.id)
          .map((popPmManager) => this.mapUserToNamedOption(popPmManager)));
      },
      error: () => {
        this.searchedPopPmManagers.set([]);
      }
    });
  }

  private retrieveProfileById(userId: string) {
    this.profileService.retrieveProfileByUserId(userId, ['id', 'employeeNr', 'initials', 'birthDate', 'phoneNumber',
      'departments { id name color staff parentDepartment { id name color } managers { id firstname lastname email roles accountStatus } employees { id firstname lastname email roles accountStatus } }',
      'team', 'functionId', 'jobScale', 'employmentDate', 'employmentEndDate', 'employmentType', 'salaryType', 'contractHours', 'fte', 'status', 'linkedInUrl', 'mySkillsPassport', 'highestEducation', 'inflowType', 'careerProfileId', 'careerTargetType',
      'coach { id email firstname lastname roles }', 'manager { id email firstname lastname roles }', 'popPmManager { id email firstname lastname roles }', 'documents { id name expirationDate achievementDate validityDuration isValid type level }', 'user { id email firstname lastname roles company { id } }']).subscribe({
      next: (response) => {
        const currentTargetUserId = this.userId() ?? this.routeUserId();
        if (currentTargetUserId !== userId) {
          return;
        }

        this.selectedProfile.set(response);
        this.selectedDepartments.set(response.departments ?? []);
        this.profileManagementSchema.setValues(response, ["user"]);
        if (this.canEditDepartment() && response.user?.company?.id) {
          this.retrieveDepartmentsByCompanyId(response.user.company.id);
        } else {
          this.selectedCoach.set(this.mapUserToNamedOption(response.coach));
          this.clearDepartments();
        }

        if (!response.coach?.id) {
          this.selectedCoach.set(null);
        } else {
          this.selectedManager.set(this.mapUserToNamedOption(response.manager));
        }
        if (!response.popPmManager?.id) {
          this.selectedPopPmManager.set(null);
        } else {
          this.selectedPopPmManager.set(this.mapUserToNamedOption(response.popPmManager));
        }
        //map the name of the coach and the manger to the firstname + lastname together
        this.selectedCoach.set(response.coach ? this.mapUserToOption(response.coach) : null);
        this.selectedManager.set(response.manager ? this.mapUserToOption(response.manager) : null);
        const sectorId = this.sectorSelectorService.getSelectedSectorId();
        const companyId = this.sectorSelectorService.getSelectedCompanyId();
        if (!this.canViewCareerProfile() || !sectorId || !companyId) {
          this.resolveSelectedCareerTarget(response.careerProfileId);
        }

        if (this.canEditCareerProfile() && response.user?.company?.id) {
          this.retrieveCoachesByCompanyId(response.user.company.id);
          this.retrievePopPmManagersByCompanyId(response.user.company.id);
          this.retrieveManagersByCompanyId(response.user.company.id);
        }
        if (response.functionId) {
          this.retrieveSelectedCareerFunction(response.functionId);
        }
        if (response.careerProfileId) {
          this.retrieveSelectedCareerTarget(response.careerProfileId);
        }
      },
    });
  }

  private retrieveDepartmentsByCompanyId(companyId: string) {
    this.departmentService.retrieveDepartmentsByCompanyId(companyId, [
      'id',
      'name',
    ]).subscribe({
      next: (response) => {
        if (this.selectedProfile()?.user?.company?.id !== companyId) {
          return;
        }
        this.departments.set(response);
        this.searchDepartments.set(response);
      },
      error: () => {
        if (this.selectedProfile()?.user?.company?.id !== companyId) {
          return;
        }
        this.departments.set([]);
        this.searchDepartments.set([]);
      },
    });
  }

  private clearDepartments() {
    this.departments.set([]);
    this.searchDepartments.set([]);
    this.selectedDepartments.set([]);
  }

  protected readonly canEditDepartment = computed(() =>
    this.currentUserService.hasAnyRole([Role.ADMIN, Role.SUPERADMIN])
  );
  protected readonly departmentDisplayItems = computed(() =>
    this.selectedProfile()?.departments ?? []
  );

  protected readonly canEditCareerProfile = computed(() =>
    this.currentUserService.hasAnyRole([Role.MANAGER, Role.ADMIN, Role.SUPERADMIN]),
  );

  protected readonly canViewCareerProfile = computed(() =>
    this.currentUserService.hasAnyRole([Role.MANAGER, Role.ADMIN, Role.SUPERADMIN, Role.COACH]),
  );

  private createProfileEntity() {
    if (this.selectedProfile()) {
      const id = this.selectedProfile()!.id;

      const employeeNr = this.formGroup()?.controls['employeeNr'].value;
      const firstname = this.formGroup()?.controls['firstname'].value;
      const lastname = this.formGroup()?.controls['lastname'].value;
      const initials = this.formGroup()?.controls['initials'].value;
      const birthDateValue = this.formGroup()?.controls['birthDate'].value;
      const birthDate = birthDateValue ? new Date(birthDateValue) : null;
      const email = this.formGroup()?.controls['email'].value;
      const phoneNumber = this.formGroup()?.controls['phoneNumber'].value;
      const canEditDepartment = this.canEditDepartment();
      const selectedDepartments = this.selectedDepartments();
      const departmentIds = canEditDepartment
        ? this.buildDepartmentIds(selectedDepartments)
        : null;
      const team = this.formGroup()?.controls['team'].value;
      const functionId = this.formGroup()?.controls['functionName-0'].value;
      const jobScale = this.formGroup()?.controls['jobScale'].value;
      const employmentDate = new Date(this.formGroup()?.controls['employmentDate'].value);
      const employmentEndDateValue = this.formGroup()?.controls['employmentEndDate'].value;
      const employmentEndDate = employmentEndDateValue ? new Date(employmentEndDateValue) : null;
      const employmentType = this.formGroup()?.controls['employmentType'].value;
      const salaryType = this.formGroup()?.controls['salaryType'].value;
      const contractHours = this.formGroup()?.controls['contractHours'].value ? parseInt(this.formGroup()?.controls['contractHours'].value) : 0;
      const fte = this.formGroup()?.controls['fte'].value ? parseFloat(this.formGroup()?.controls['fte'].value) : 0;
      const status = this.formGroup()?.controls['status'].value;
      const linkedInUrl = this.formGroup()?.controls['linkedInUrl'].value;
      const mySkillsPassport = this.selectedProfile()!.mySkillsPassport;
      const highestEducation = this.formGroup()?.controls['highestEducation'].value;
      const inflowType = this.formGroup()?.controls['inflowType'].value;

      const selectedCareerProfileId = this.formGroup()?.controls['careerProfile-0']?.value ?? "";
      const careerTargetType = selectedCareerProfileId ? CareerTargetKind.CAREER_PROFILE : null;
      const documents = this.getDocumentsArray();

      const user = new UserDto(this.selectedProfile()!.user.id, firstname, lastname, email, [Role.TRAINEE], null, AccountStatus.INACTIVE, null!);

      const selectedCoach = this.selectedCoach();
      const selectedCoachInput = selectedCoach
        ? new UserDto(
          selectedCoach.id,
          selectedCoach.firstname,
          selectedCoach.lastname,
          selectedCoach.email,
          selectedCoach.roles,
          selectedCoach.keycloakId,
          selectedCoach.accountStatus,
          selectedCoach.company,
        )
        : null;
      const selectedManager = this.selectedManager();
      const selectedManagerInput = selectedManager
        ? new UserDto(
          selectedManager.id,
          selectedManager.firstname,
          selectedManager.lastname,
          selectedManager.email,
          selectedManager.roles,
          selectedManager.keycloakId,
          selectedManager.accountStatus,
          selectedManager.company,
        )
        : null;
      const selectedPopPmManager = this.selectedPopPmManager();
      const selectedPopPmManagerInput = selectedPopPmManager
        ? new UserDto(
          selectedPopPmManager.id,
          selectedPopPmManager.firstname,
          selectedPopPmManager.lastname,
          selectedPopPmManager.email,
          selectedPopPmManager.roles,
          selectedPopPmManager.keycloakId,
          selectedPopPmManager.accountStatus,
          selectedPopPmManager.company,
        )
        : null;

      return new ProfileDto(
        id, user, employeeNr, initials, birthDate, phoneNumber, team,
        functionId, jobScale, employmentDate, employmentEndDate, employmentType,
        salaryType, contractHours, fte, status, linkedInUrl, mySkillsPassport,
        highestEducation, inflowType, selectedCareerProfileId, careerTargetType,
        selectedCoachInput, selectedManagerInput, selectedPopPmManagerInput, documents, departmentIds
      );
    }
    this.buttonService.enableButton(this.profileManagementButton);
    this.alertService.createAlert(new Alert(AlertType.WARN, "", "Niet alle velden zijn juist ingevuld", false));
    throw new ProfileUndefinedException("Profile not found");
  }

  private getDocumentsArray() {
    const formArray = this.formGroup()?.get('documents') as FormArray;
    return formArray ? formArray.value.map((doc: any) => doc.document) : [];
  }

  private getDocumentsFilesArray() {
    const formArray = this.formGroup()?.get('documents') as FormArray;
    return formArray ? formArray.value.map((doc: any) => doc.fileUrl) : [];
  }

  private buildDepartmentIds(departments: DepartmentDto[]): string[] {
    return departments.map(department => department.id!);
  }

  private getStringArray(controlKey: string) {
    const keys = Object.keys(this.formGroup()!.controls).filter(key => key.includes(controlKey));
    const values = [];

    for (let i = 0; i < keys.length; i++) {
      values.push(this.formGroup()?.controls[keys[i]].value);
    }

    return values;
  }

  private retrieveCareerProfilesByCompanyId(sectorId: string, companyId: string) {
    const selectedCareerTargetId = this.selectedProfile()?.careerProfileId;
    this.careerProfileService.retrieveCareerProfilesBySectorIdAndCompanyId(sectorId, companyId, this.itemsPerPage, ['id', 'name'], selectedCareerTargetId).subscribe({
      next: (response) => {
        this.defaultCareerTargets.set(response);
      }
    });
  }

  private resolveSelectedCareerTarget(id?: string | null) {
    if (!id) {
      this.selectedCareerTarget.set(undefined);
      return;
    }

    const selectedCareerTarget = this.findCareerTargetById(id);
    if (selectedCareerTarget) {
      this.selectedCareerTarget.set(selectedCareerTarget);
      return;
    }

    this.selectedCareerTarget.set(undefined);
  }

  private findCareerTargetById(id?: string | null): CareerProfileDto | undefined {
    if (!id) {
      return undefined;
    }

    return this.defaultCareerTargets().find((target) => target.id === id)
      ?? this.careerTargets().find((target) => target.id === id);
  }

  private retrieveCoachesByCompanyId(companyId: string) {
    this.userService.retrieveUsersByCompanyId(
      companyId,
      0,
      this.itemsPerPage,
      [Role.COACH],
      ['id', 'firstname', 'lastname', 'email', 'roles'],
    ).subscribe({
      next: (response) => {
        this.availableCoaches.set(response.map((user) => this.mapUserToOption(user)));
      },
      error: () => {
        this.availableCoaches.set([]);
      },
    });
  }

  private retrieveManagersByCompanyId(companyId: string) {
    this.userService.retrieveUsersByCompanyId(
      companyId,
      0,
      this.itemsPerPage,
      [Role.MANAGER],
      ['id', 'firstname', 'lastname', 'email', 'roles'],
    ).subscribe({
      next: (response) => {
        this.availableManagers.set(response.map((user) => this.mapUserToOption(user)));
      },
      error: () => {
        this.availableManagers.set([]);
      },
    });
  }

  private retrievePopPmManagersByCompanyId(companyId: string) {
    this.userService.retrieveUsersByCompanyId(
      companyId,
      0,
      this.itemsPerPage,
      [Role.POPPMMANAGER],
      ['id', 'firstname', 'lastname', 'email', 'roles'],
    ).subscribe({
      next: (response) => {
        const popPmManagerOptions = response
          .filter((popPmManager): popPmManager is UserDto => !!popPmManager?.id)
          .map((popPmManager) => this.mapUserToNamedOption(popPmManager));
        this.availablePopPmManagers.set(popPmManagerOptions);
      },
      error: () => {
        this.availablePopPmManagers.set([]);
      },
    });
  }

  private mapUserToNamedOption(user: UserDto | null): UserDto & { name: string } | null {
    if (user) {
      return {
        ...user,
        name: `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim()
      }
    }
    return null;
  }

  private mapUserToOption(user: UserDto): UserOption {
    return {
      ...user,
      name: [user.firstname, user.lastname].filter(Boolean).join(' '),
    };
  }

  protected getDocumentsByType(documents: DocumentDto[] = [], type: DocumentType): DocumentDto[] {
    return documents?.filter((doc) => doc.type === type) ?? [];
  }

  protected getDocumentLevelLabel(level: DocumentLevel): string {
    const labels: Record<DocumentLevel, string> = {
      [DocumentLevel.NA]: 'N.v.t.',
      [DocumentLevel.MBO1]: 'MBO 1',
      [DocumentLevel.MBO2]: 'MBO 2',
      [DocumentLevel.MBO3]: 'MBO 3',
      [DocumentLevel.MBO4]: 'MBO 4',
      [DocumentLevel.HBO]: 'HBO',
      [DocumentLevel.WO]: 'WO',
    };
    return labels[level];
  }

  protected openDocument(doc: DocumentDto): void {
    const userId = this.userId() ?? this.routeUserId();
    if (!doc.id || !userId) {
      return;
    }
    this.profileService.retrieveDocumentUrl(userId, Number(doc.id)).subscribe({
      next: (url) => {
        if (url) window.open(url, '_blank');
      }
    });
  }

  protected isReadonly(): boolean {
    return this.readonlyMode() || !this.isEditing;
  }

  protected showEditButton(): boolean {
    return !this.readonlyMode() && !this.isEditing;
  }

  protected showSaveActions(): boolean {
    return !this.readonlyMode() && this.isEditing;
  }

  protected toggleEditing(): void {
    if (this.readonlyMode()) {
      return;
    }

    this.isEditing = !this.isEditing;
    const formGroup = this.formGroup();
    if (!formGroup) {
      return;
    }

    if (this.isEditing) {
      formGroup.enable({emitEvent: false});
      return;
    }

    formGroup.disable({emitEvent: false});
  }

  private retrieveCareerFunctionsByCompanyId() {
    const companyId = this.sectorSelectorService.getSelectedCompanyId();
    if (companyId) {
      this.careerFunctionService.retrieveCompanyCareerFunctionsByCompanyId(companyId, 0, 25,
        ['id', 'name']).subscribe({
        next: (result) => {
          this.defaultCareerFunctions.set(result);
        }
      });
    }
  }

  private retrieveSelectedCareerFunction(id: string) {
    this.careerFunctionService.retrieveCareerFunctionById(id, ['id', 'name']).subscribe({
      next: (result) => {
        this.selectedCareerFunction.set(result);
      }
    });
  }

  private retrieveSelectedCareerTarget(id: string) {
    this.careerProfileService.retrieveCareerProfileById(id, ['id', 'name']).subscribe({
      next: (result) => {
        this.selectedCareerTarget.set(result);
      }
    });
  }

  public hasUnsavedChanges(): boolean {
    return this.isEditing && (this.formGroup()?.dirty ?? false);
  }
}
